import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar } from '../components/Avatar'
import { TopBar } from '../components/TopBar'
import { useApp } from '../store/AppContext'
import { profilePhotoFor, removeProfilePhoto, resizeProfilePhoto, saveProfilePhoto } from '../lib/profilePhotos'

function CameraIcon() {
  return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M4 8.5h3l1.5-2h7l1.5 2h3v10H4z"/><circle cx="12" cy="13.5" r="3.2"/></svg>
}

export function AdultParticipantProfile() {
  const { currentUser } = useApp()
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [photoVersion, setPhotoVersion] = useState(0)
  const [photoError, setPhotoError] = useState('')
  const [photoBusy, setPhotoBusy] = useState(false)
  const hasPhoto = useMemo(() => Boolean(profilePhotoFor(currentUser.id)), [currentUser.id, photoVersion])

  const choosePhoto = async (file?: File) => {
    if (!file) return
    setPhotoError('')
    setPhotoBusy(true)
    try {
      const dataUrl = await resizeProfilePhoto(file)
      saveProfilePhoto(currentUser.id, dataUrl)
      setPhotoVersion((value) => value + 1)
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : 'Could not upload that photo.')
    } finally {
      setPhotoBusy(false)
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const removePhoto = () => {
    removeProfilePhoto(currentUser.id)
    setPhotoVersion((value) => value + 1)
    setPhotoError('')
  }

  return <div className="min-h-screen bg-white pb-28 text-slate-900">
    <TopBar title="My Profile" subtitle="About me, wellbeing and the people around me" />
    <div className="space-y-4 px-3.5 py-4">
      <section className="rounded-[24px] border border-[#cfdde9] bg-white p-4 shadow-[0_8px_24px_rgba(55,83,110,.055)]">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0 rounded-full border-[3px] border-[#6f93b8] p-[2px] shadow-[0_3px_10px_rgba(69,101,133,.1)]">
            <Avatar key={photoVersion} user={currentUser} size={64}/>
            <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoBusy} aria-label="Change profile photo" className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-white bg-[#6f93b8] text-white shadow-sm disabled:opacity-60">
              {photoBusy ? <span className="text-xs font-black">…</span> : <CameraIcon />}
            </button>
            <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(event) => void choosePhoto(event.target.files?.[0])} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-extrabold uppercase tracking-[.12em] text-[#7897b5]">My profile</p>
            <h1 className="truncate text-[21px] font-black text-[#27384a]">{currentUser.name}</h1>
            <p className="mt-1 text-[13px] font-medium leading-5 text-[#778594]">Your photo and personal information stay with your participant profile.</p>
            <div className="mt-2 flex flex-wrap gap-3">
              <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoBusy} className="text-[12px] font-bold text-[#6887a5]">{hasPhoto ? 'Change photo' : 'Add photo'}</button>
              {hasPhoto && <button type="button" onClick={removePhoto} className="text-[12px] font-bold text-[#8a959f]">Remove</button>}
            </div>
            {photoError && <p className="mt-2 text-[12px] font-semibold text-rose-600">{photoError}</p>}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3">
        <Link to="/about-me" className="rounded-[20px] border border-[#d5e1eb] bg-white p-4 shadow-[0_5px_18px_rgba(15,23,42,.04)]">
          <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-[#f0ecfb] text-[#7558b5]">♡</div>
          <p className="mt-3 text-sm font-black">About Me</p>
          <p className="mt-1 text-[10px] font-semibold leading-4 text-slate-500">What matters to me, communication, routines and support preferences.</p>
        </Link>
        <Link to="/team" className="rounded-[20px] border border-[#d5e1eb] bg-white p-4 shadow-[0_5px_18px_rgba(15,23,42,.04)]">
          <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-[#e8f7f5] text-[#249a96]">◎</div>
          <p className="mt-3 text-sm font-black">My Team</p>
          <p className="mt-1 text-[10px] font-semibold leading-4 text-slate-500">The people I’ve chosen to support me.</p>
        </Link>
      </section>

      <section className="rounded-[24px] border border-[#d5e1eb] bg-white p-4 shadow-[0_5px_18px_rgba(15,23,42,.04)]">
        <p className="text-[10px] font-black uppercase tracking-[.12em] text-[#6887a5]">My wellbeing</p>
        <h2 className="mt-1 text-lg font-black">How I’m going</h2>
        <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">This is the place for personal wellbeing and participant information. App appearance, notifications and account controls live in Settings.</p>
      </section>

      <Link to="/about-me" className="flex items-center gap-3 rounded-[20px] border border-[#d5e1eb] bg-[#f7fafc] p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#6887a5]"><svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 20h4l11-11-4-4L4 16v4Z"/><path d="m13.5 6.5 4 4"/></svg></div>
        <div className="min-w-0 flex-1"><p className="text-sm font-black">Update About Me</p><p className="text-xs font-semibold text-slate-500">Edit my preferences and support information</p></div><span className="text-slate-400">→</span>
      </Link>
    </div>
  </div>
}

